import React, { useCallback, useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
  Alert,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import { format, parseISO } from 'date-fns';
import Icon from 'react-native-vector-icons/Ionicons';

import { RootState, AppDispatch } from '@/store';
import {
  getMyTimeOff,
  getPendingTimeOff,
  cancelTimeOff,
  approveTimeOff,
  denyTimeOff,
  clearError,
  TimeOffRequest,
} from '@/store/slices/timeOffSlice';
import { colors, font, space, radius, shadow } from '@/theme/chalkCourt';
import LoadingScreen from '@/components/common/LoadingScreen';
import RequestTimeOffModal from './RequestTimeOffModal';

interface StatusStyle {
  label: string;
  color: string;
  bg: string;
}

const STATUS: Record<string, StatusStyle> = {
  CREATED: { label: 'DRAFT', color: colors.inkSoft, bg: colors.surfaceSunk },
  PENDING_APPROVAL: { label: 'PENDING', color: colors.warn, bg: colors.warnSoft },
  APPROVED: { label: 'APPROVED', color: colors.court, bg: colors.courtSoft },
  DENIED: { label: 'DENIED', color: colors.accentDark, bg: colors.accentSoft },
  CANCELLED: { label: 'CANCELLED', color: colors.inkFaint, bg: colors.surfaceSunk },
};

const statusStyle = (status: string): StatusStyle =>
  STATUS[status] ?? { label: status, color: colors.inkSoft, bg: colors.surfaceSunk };

const fmtRange = (start: string, end: string): string => {
  try {
    const s = parseISO(start);
    const e = parseISO(end);
    if (start === end) {
      return format(s, 'EEE, MMM d');
    }
    return `${format(s, 'MMM d')} – ${format(e, 'MMM d')}`;
  } catch {
    return `${start} – ${end}`;
  }
};

const TimeOffScreen: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigation = useNavigation<any>();
  const { myRequests, pendingRequests, isApprover, isLoading, error } = useSelector(
    (state: RootState) => state.timeOff
  );

  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(() => {
    dispatch(getMyTimeOff());
    dispatch(getPendingTimeOff());
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (error) {
      Alert.alert('Time Off', error, [{ text: 'OK', onPress: () => dispatch(clearError()) }]);
    }
  }, [error, dispatch]);

  const onCancel = (req: TimeOffRequest) => {
    Alert.alert('Cancel request', `Cancel your time off for ${fmtRange(req.start, req.end)}?`, [
      { text: 'Keep it', style: 'cancel' },
      {
        text: 'Cancel request',
        style: 'destructive',
        onPress: () => dispatch(cancelTimeOff(req.id)).then(load),
      },
    ]);
  };

  const onDecide = (req: TimeOffRequest, approve: boolean) => {
    const verb = approve ? 'Approve' : 'Deny';
    Alert.alert(
      `${verb} request`,
      `${verb} ${req.employeeName ?? 'this employee'}'s time off for ${fmtRange(
        req.start,
        req.end
      )}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: verb,
          onPress: () =>
            dispatch(approve ? approveTimeOff(req.id) : denyTimeOff(req.id)).then(load),
        },
      ]
    );
  };

  if (isLoading && myRequests.length === 0) {
    return <LoadingScreen message="Loading time off…" />;
  }

  return (
    <>
      <ScrollView
        style={styles.screen}
        contentContainerStyle={styles.content}
        refreshControl={
          <RefreshControl
            refreshing={isLoading}
            onRefresh={load}
            tintColor={colors.accent}
            colors={[colors.accent]}
          />
        }>
        <View style={styles.header}>
          <Text style={styles.kicker}>YOUR REQUESTS</Text>
          <Text style={styles.headline}>TIME OFF</Text>
          <View style={styles.rule} />
        </View>

        <TouchableOpacity
          style={styles.requestBtn}
          activeOpacity={0.85}
          onPress={() => setModalOpen(true)}>
          <Icon name="add-circle" size={22} color={colors.accent} />
          <Text style={styles.requestBtnText}>REQUEST TIME OFF</Text>
        </TouchableOpacity>

        {/* Approver queue */}
        {isApprover && (
          <>
            <Text style={styles.sectionTitle}>{`Approvals · ${pendingRequests.length}`}</Text>
            {pendingRequests.length === 0 ? (
              <Text style={styles.emptyLine}>Nothing waiting on you.</Text>
            ) : (
              pendingRequests.map((req) => (
                <View key={req.id} style={[styles.card, styles.approvalCard]}>
                  <Text style={styles.cardName}>{req.employeeName ?? 'Employee'}</Text>
                  <Text style={styles.cardRange}>
                    {fmtRange(req.start, req.end)} · {req.days} {req.days === 1 ? 'day' : 'days'}
                  </Text>
                  {!!req.notes && <Text style={styles.cardNotes}>{req.notes}</Text>}
                  <View style={styles.actionRow}>
                    <TouchableOpacity
                      style={[styles.actBtn, styles.denyBtn]}
                      activeOpacity={0.85}
                      onPress={() => onDecide(req, false)}>
                      <Text style={styles.denyText}>DENY</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.actBtn, styles.approveBtn]}
                      activeOpacity={0.85}
                      onPress={() => onDecide(req, true)}>
                      <Text style={styles.approveText}>APPROVE</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              ))
            )}
          </>
        )}

        {/* My requests */}
        <Text style={styles.sectionTitle}>My requests</Text>
        {myRequests.length === 0 ? (
          <View style={styles.empty}>
            <Icon name="airplane-outline" size={34} color={colors.inkFaint} />
            <Text style={styles.emptyTitle}>No time off requested</Text>
            <Text style={styles.emptyBody}>
              Tap “Request time off” to submit days away for approval.
            </Text>
          </View>
        ) : (
          myRequests.map((req) => {
            const s = statusStyle(req.status);
            const isDraft = req.status === 'CREATED';
            return (
              <TouchableOpacity
                key={req.id}
                style={styles.card}
                activeOpacity={isDraft ? 1 : 0.7}
                disabled={isDraft}
                onPress={() =>
                  isDraft ? undefined : navigation.navigate('TimeOffDetail', { requestId: req.id })
                }>
                <View style={styles.cardHead}>
                  <Text style={styles.cardRangeStrong}>{fmtRange(req.start, req.end)}</Text>
                  <View style={[styles.pill, { backgroundColor: s.bg }]}>
                    <Text style={[styles.pillText, { color: s.color }]}>{s.label}</Text>
                  </View>
                </View>
                <Text style={styles.cardDays}>
                  {req.days} {req.days === 1 ? 'day' : 'days'}
                  {req.approvedByName ? ` · reviewed by ${req.approvedByName}` : ''}
                </Text>
                {!!req.notes && <Text style={styles.cardNotes}>{req.notes}</Text>}
                {isDraft && (
                  <TouchableOpacity
                    style={styles.continueLink}
                    activeOpacity={0.7}
                    onPress={() => navigation.navigate('TimeOffAssemble', { requestId: req.id })}>
                    <Icon name="arrow-forward-circle" size={18} color={colors.accent} />
                    <Text style={styles.continueLinkText}>Continue — pick coverage</Text>
                  </TouchableOpacity>
                )}
                {req.cancellable && (
                  <TouchableOpacity
                    style={styles.cancelLink}
                    activeOpacity={0.7}
                    onPress={() => onCancel(req)}>
                    <Icon name="close-circle-outline" size={16} color={colors.accentDark} />
                    <Text style={styles.cancelLinkText}>Cancel request</Text>
                  </TouchableOpacity>
                )}
              </TouchableOpacity>
            );
          })
        )}

        <Text style={styles.footnote}>
          Approved time off is visible to managers in iflipweb and on your schedule.
        </Text>
      </ScrollView>

      <RequestTimeOffModal
        visible={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmitted={load}
      />
    </>
  );
};

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: colors.bg },
  content: { padding: space.lg, paddingBottom: space.xxl },

  header: { marginBottom: space.lg },
  kicker: {
    fontFamily: font.bodyBold,
    fontSize: 13,
    letterSpacing: 2.5,
    color: colors.accent,
    marginBottom: -2,
  },
  headline: {
    fontFamily: font.display,
    fontSize: 56,
    lineHeight: 58,
    color: colors.ink,
    letterSpacing: -1,
  },
  rule: {
    height: 3,
    width: 48,
    backgroundColor: colors.ink,
    borderRadius: 2,
    marginTop: space.sm,
  },

  requestBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: 54,
    borderRadius: radius.md,
    backgroundColor: colors.surface,
    borderWidth: 2,
    borderColor: colors.accent,
    marginBottom: space.lg,
  },
  requestBtnText: {
    fontFamily: font.display,
    fontSize: 21,
    letterSpacing: 1,
    color: colors.accent,
    marginLeft: space.sm,
  },

  sectionTitle: {
    fontFamily: font.bodyBold,
    fontSize: 12,
    letterSpacing: 1.8,
    textTransform: 'uppercase',
    color: colors.inkSoft,
    marginBottom: space.md,
    marginTop: space.xs,
  },
  emptyLine: {
    fontFamily: font.bodyMed,
    fontSize: 14,
    color: colors.inkFaint,
    marginBottom: space.lg,
  },

  card: {
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    padding: space.lg,
    marginBottom: space.md,
    borderWidth: 1,
    borderColor: colors.line,
  },
  approvalCard: {
    borderColor: '#E8D49B',
    ...shadow.card,
  },
  cardHead: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cardName: {
    fontFamily: font.displayBold,
    fontSize: 22,
    color: colors.ink,
  },
  cardRange: {
    fontFamily: font.bodyMed,
    fontSize: 14,
    color: colors.inkSoft,
    marginTop: 2,
  },
  cardRangeStrong: {
    fontFamily: font.displayBold,
    fontSize: 22,
    color: colors.ink,
    flex: 1,
  },
  cardDays: {
    fontFamily: font.bodyMed,
    fontSize: 13.5,
    color: colors.inkSoft,
    marginTop: 2,
  },
  cardNotes: {
    fontFamily: font.body,
    fontSize: 13,
    lineHeight: 18,
    color: colors.inkFaint,
    marginTop: space.sm,
  },
  pill: {
    borderRadius: radius.pill,
    paddingVertical: 4,
    paddingHorizontal: 10,
    marginLeft: space.sm,
  },
  pillText: {
    fontFamily: font.bodyBold,
    fontSize: 10.5,
    letterSpacing: 1.4,
  },

  actionRow: {
    flexDirection: 'row',
    gap: space.md,
    marginTop: space.md,
  },
  actBtn: {
    flex: 1,
    height: 46,
    borderRadius: radius.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  approveBtn: { backgroundColor: colors.court },
  approveText: {
    fontFamily: font.bodyBold,
    fontSize: 14,
    letterSpacing: 1,
    color: colors.onAccent,
  },
  denyBtn: {
    backgroundColor: colors.surface,
    borderWidth: 1.5,
    borderColor: colors.lineStrong,
  },
  denyText: {
    fontFamily: font.bodyBold,
    fontSize: 14,
    letterSpacing: 1,
    color: colors.inkSoft,
  },

  continueLink: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: space.md,
    alignSelf: 'flex-start',
  },
  continueLinkText: {
    fontFamily: font.bodyBold,
    fontSize: 13,
    color: colors.accent,
    marginLeft: 4,
  },
  cancelLink: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: space.md,
    alignSelf: 'flex-start',
  },
  cancelLinkText: {
    fontFamily: font.bodySemi,
    fontSize: 13,
    color: colors.accentDark,
    marginLeft: 4,
  },

  empty: {
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    padding: space.xl,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.line,
  },
  emptyTitle: {
    fontFamily: font.displayBold,
    fontSize: 22,
    color: colors.ink,
    marginTop: space.sm,
  },
  emptyBody: {
    fontFamily: font.body,
    fontSize: 14,
    lineHeight: 20,
    color: colors.inkSoft,
    textAlign: 'center',
    marginTop: 2,
  },

  footnote: {
    fontFamily: font.body,
    fontSize: 12,
    lineHeight: 17,
    color: colors.inkFaint,
    textAlign: 'center',
    marginTop: space.md,
    paddingHorizontal: space.lg,
  },
});

export default TimeOffScreen;
